<template>
  <div v-if="isOpen" class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <div class="title-wrapper">
          <h2>{{ title }}</h2>
          <span v-if="count > 0" class="selected-count">
            {{ count }}
          </span>
        </div>
        <button class="close-button" @click="$emit('close')">
          <span class="close-icon"></span>
        </button>
      </div>
      <div class="modal-controls">
        <button 
          class="reset-button" 
          :class="{ 'reset-button--disabled': count === 0 }"
          :disabled="count === 0"
          @click="$emit('reset')"
        >
          {{ labels.resetFilters }}
        </button>
        <div class="controls-divider" v-if="!hideSearch"></div>
        <div class="search-wrapper" v-if="!hideSearch">
          <span class="search-icon"></span>
          <input 
            type="text"
            placeholder="Search"
            v-model="searchQuery"
            @input="$emit('search', searchQuery)"
          >
          <button 
            v-if="searchQuery"
            class="clear-button" 
            @click="clearSearch"
          >
            <span class="close-icon"></span>
          </button>
        </div>
      </div>
      <div class="modal-content">
        <slot></slot>
      </div>
      <div class="modal-footer">
        <slot name="modal-footer"></slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Modal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    count: {
      type: Number,
      default: 0
    },
    hideSearch: Boolean,
    labels: Object
  },
  data() {
    return {
      searchQuery: ''
    }
  },
  methods: {
    clearSearch() {
      this.searchQuery = ''
      this.$emit('search', '')
    }
  },
  emits: ['close', 'reset', 'search', 'apply']
}
</script>

<style scoped>
.modal-overlay {
  position: absolute;
  z-index: 1000;
  top: 40px;
}

.modal {
  background: white;
  width: 400px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 16px 0px 16px;
}

.title-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.modal-header h2 {
  margin: 0;
  font-size: 18px;
  line-height: 1;
  display: inline-block;
  color: #323232;
  padding-bottom: 4px;
}

.selected-count {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #2655ff;
  color: white;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 500;
  margin-top: 4px;
}

.close-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
}

.close-icon {
  width: 16px;
  height: 16px;
  display: block;
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.9844 1.41241L12.5876 0.015625L7 5.60322L1.41241 0.015625L0.015625 1.41241L5.60322 7L0.015625 12.5876L1.41241 13.9844L7 8.39678L12.5876 13.9844L13.9844 12.5876L8.39678 7L13.9844 1.41241Z' fill='%23323232'/%3E%3C/svg%3E%0A");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.modal-controls {
  display: flex;
  align-items: center;
  padding: 16px;
}

.reset-button {
  border: none;
  background: none;
  color: #2655ff;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
  min-width: fit-content;
  font-family: inherit;
  font-weight: 500;
  font-size: 14px;
}

.reset-button--disabled {
  color: #cccccc;
  cursor: not-allowed;
}

.controls-divider {
  width: 1px;
  height: 24px;
  background-color: #eee;
  margin: 0 16px;
}

.search-wrapper {
  flex-grow: 1;
  position: relative;
  display: flex;
  align-items: center;
  padding-bottom: 4px;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease;
}

.search-wrapper input {
  width: 100%;
  border: none;
  outline: none;
  font-size: 14px;
  margin-left: 8px;
}

.search-wrapper input::placeholder {
  opacity: 0;
  transition: opacity 0.2s;
}

.search-wrapper:has(input:focus) {
  border-bottom-color: #323232;
}

.search-wrapper input:focus::placeholder {
  opacity: 1;
}

.search-icon {
  width: 16px;
  height: 16px;
  display: block;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15.8084 14.842L11.3595 10.3515C12.2301 9.26009 12.755 7.87712 12.755 6.37334C12.755 2.85342 9.90159 0 6.37751 0C2.85342 0 0 2.85759 0 6.37751C0 9.89742 2.85342 12.7508 6.37751 12.7508C7.90211 12.7508 9.29758 12.2177 10.3973 11.3262L14.817 15.7876C14.9503 15.9292 15.1336 16 15.3127 16C15.4835 16 15.6543 15.9375 15.7834 15.8125C16.0583 15.5501 16.0666 15.1169 15.8084 14.842ZM6.37751 11.3804C5.04035 11.3804 3.78235 10.8597 2.83676 9.91409C1.89117 8.9685 1.37048 7.71049 1.37048 6.37751C1.37048 5.04035 1.89117 3.78235 2.83676 2.84093C3.78235 1.89534 5.04035 1.37464 6.37751 1.37464C7.71466 1.37464 8.97266 1.89534 9.91825 2.84093C10.8638 3.78651 11.3845 5.04452 11.3845 6.37751C11.3845 7.71466 10.8638 8.97266 9.91825 9.91409C8.97266 10.8597 7.71466 11.3804 6.37751 11.3804Z' fill='%23323232'/%3E%3C/svg%3E ");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.modal-content {
  max-height: 300px;
  overflow-y: auto;
  padding: 16px;
}

.modal-content :deep(.filter-option) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.modal-content :deep(.option-count) {
  color: #323232;
}

.modal-footer {
  padding: 16px;
}

.clear-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-button .close-icon {
  width: 12px;
  height: 12px;
}

@media screen and (max-width: 768px) {
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: 0;
    background-color: white;
  }

  .modal {
    width: 100%;
    height: 100%;
    box-shadow: none;
  }

  .modal-content {
    height: calc(100% - 180px);
    max-height: none;
  }
}
</style> 